import React, { useState, useEffect } from 'react';
import { Plus, Calendar, MapPin, Eye, Edit, Trash2, Database, ListChecks } from 'lucide-react';
import { eventsAPI } from '../services/api';
import tokenManager from '../utils/tokenManager';
import toast from 'react-hot-toast';
import EventModal from './EventModal';
import EventChecklistModal from './EventChecklistModal';

const QuickNotesCell = ({ event, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(event.description || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNotes(event.description || '');
  }, [event.description]);

  const save = async () => {
    if (notes === (event.description || '')) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      const response = await eventsAPI.update(event.id, { ...event, description: notes });
      onSave(response.data);
      setIsEditing(false);
      toast.success('Quick notes saved');
    } catch (error) {
      toast.error('Failed to save quick notes');
      console.error('Error saving quick notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const cancel = () => {
    setNotes(event.description || '');
    setIsEditing(false);
  };

  return (
    <td onClick={(e) => e.stopPropagation()} style={{ fontSize: '12px', minWidth: '220px' }}>
      {isEditing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <textarea
            autoFocus
            className="form-control"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancel();
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') save();
            }}
            rows={3}
            placeholder="Add a quick note"
          />
          <div style={{ display: 'flex', gap: '4px' }}>
            <button className="btn btn-secondary" type="button" onClick={cancel} disabled={isSaving}>Cancel</button>
            <button className="btn" type="button" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="btn btn-secondary"
          type="button"
          onClick={() => setIsEditing(true)}
          style={{ width: '100%', minHeight: '32px', padding: '6px 8px', textAlign: 'left', whiteSpace: 'normal' }}
          title="Edit quick notes"
        >
          {event.description || 'Add quick note'}
        </button>
      )}
    </td>
  );
};

const EventList = ({ onOpenRiskAnalysis, onOpenEffectsDatabase }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [checklistEvent, setChecklistEvent] = useState(null);
  const [travelPlans, setTravelPlans] = useState({});

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsAPI.getAll();
      // Sort events by date (earliest first)
      const sortedEvents = response.data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(sortedEvents);
      try {
        const travelResponse = await fetch('/api/events/travel-plan', {
          headers: { 'Authorization': `Bearer ${tokenManager.getToken()}` }
        });
        if (travelResponse.ok) setTravelPlans(await travelResponse.json());
      } catch (travelError) {
        console.error('Travel plans unavailable:', travelError);
      }
    } catch (error) {
      toast.error('Failed to load events');
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setShowModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setShowModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await eventsAPI.delete(eventId);
      toast.success('Event deleted successfully');
      loadEvents();
    } catch (error) {
      toast.error('Failed to delete event');
      console.error('Error deleting event:', error);
    }
  };

  const handleSaveEvent = async (eventData) => {
    try {
      // Convert date to ISO string if it's a Date object
      const processedData = {
        ...eventData,
        date: eventData.date instanceof Date ? eventData.date.toISOString() : eventData.date
      };
      
      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, processedData);
        toast.success('Event updated successfully');
      } else {
        await eventsAPI.create(processedData);
        toast.success('Event created successfully');
      }
      setShowModal(false);
      loadEvents();
    } catch (error) {
      toast.error('Failed to save event');
      console.error('Error saving event:', error);
    }
  };

  const handleQuickNotesSave = (updatedEvent) => {
    setEvents((currentEvents) => currentEvents.map((event) => (
      event.id === updatedEvent.id ? updatedEvent : event
    )));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAllDates = (event) => {
    const primaryDate = formatDate(event.date);

    if (!event.additional_dates) {
      return primaryDate;
    }

    try {
      const additionalDates = JSON.parse(event.additional_dates);
      if (!additionalDates || additionalDates.length === 0) {
        return primaryDate;
      }

      // Combine all dates, sort them, and format
      const allDates = [new Date(event.date), ...additionalDates.map(d => new Date(d))];
      allDates.sort((a, b) => a - b);

      // Format each date
      const formattedDates = allDates.map(d => d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit'
      }));

      // Get year from first date
      const year = allDates[0].getFullYear();

      return `${formattedDates.join(', ')}/${year}`;
    } catch (e) {
      return primaryDate;
    }
  };

  const getDateStatus = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    
    // Reset time to compare dates only
    eventDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'past'; // Past date
    if (diffDays === 0) return 'today'; // Today
    return 'future'; // Future date
  };

  const isHistoric = (dateString) => {
    const eventDate = new Date(dateString);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return eventDate < oneMonthAgo;
  };

  const reviewLabel = (status) => ({
    confirmed: 'AI checked',
    needs_info: 'AI: needs info',
    source_conflict: 'AI: conflict',
    suggestion: 'AI: suggestion',
    needs_review: 'AI: review pending'
  }[status]);

  const reviewColor = (status) => ({
    confirmed: '#15803d',
    needs_info: '#b45309',
    source_conflict: '#b91c1c',
    suggestion: '#1d4ed8',
    needs_review: '#7c3aed'
  }[status] || '#64748b');

  const TravelCell = ({ event }) => {
    const plan = travelPlans[event.id];
    if (!plan) return <td style={{ fontSize: '12px', color: '#94a3b8' }}>—</td>;
    if (plan.status !== 'ready') return <td style={{ fontSize: '12px', color: '#f59e0b' }}>{plan.message}</td>;
    return (
      <td style={{ fontSize: '12px', minWidth: '180px' }}>
        <div style={{ fontWeight: 600 }}>{plan.message}</div>
        <div style={{ color: '#94a3b8', marginTop: '2px' }}>{plan.origin} · {plan.distance_km} km · {Math.floor(plan.drive_minutes / 60)}h {plan.drive_minutes % 60}m{plan.break_minutes ? ' + break' : ''}</div>
        {plan.destination_is_approximate && <div style={{ color: '#f59e0b', marginTop: '2px' }}>Approximate route — using event town/area</div>}
        {plan.overnight_recommendation && <div style={{ color: '#38bdf8', marginTop: '4px' }}>{plan.overnight_recommendation.message}</div>}
      </td>
    );
  };

  // Separate events into current and historic
  const currentEvents = events
    .filter(event => !isHistoric(event.date))
    .sort((a, b) => {
      const aPast = getDateStatus(a.date) === 'past';
      const bPast = getDateStatus(b.date) === 'past';
      if (aPast !== bPast) return aPast ? 1 : -1;
      return new Date(a.date) - new Date(b.date);
    });
  const historicEvents = events
    .filter(event => isHistoric(event.date))
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  if (loading) {
    return (
      <div className="text-center">
        <p>Loading events...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2>Events</h2>
            <div className="event-legend">
              <span><i className="event-status-swatch past"></i>
                Past events
              </span>
              <span><i className="event-status-swatch today"></i>
                Today
              </span>
              <span><i className="event-status-swatch future"></i>
                Future events
              </span>
            </div>
          </div>
          <button className="btn" onClick={handleCreateEvent}>
            <Plus size={16} className="mr-2" />
            New Event
          </button>
        </div>

        {events.length === 0 ? (
          <div className="text-center">
            <p>No events found. Create your first event to get started.</p>
            <button className="btn mt-4" onClick={handleCreateEvent}>
              <Plus size={16} className="mr-2" />
              Create Event
            </button>
          </div>
        ) : (
          <>
            {/* Current Events */}
            {currentEvents.length > 0 && (
              <div className="events-table-container" style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '15px' }}>Current Events</h3>
                <table className="table events-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '200px' }}>Name</th>
                      <th style={{ minWidth: '120px' }}>Date</th>
                      <th style={{ minWidth: '180px' }}>Location</th>
                      <th style={{ minWidth: '180px' }}>Supplier</th>
                      <th style={{ minWidth: '180px' }}>Technician</th>
                      <th style={{ minWidth: '180px' }}>Travel</th>
                      <th style={{ minWidth: '220px' }}>Quick Notes</th>
                      <th style={{ minWidth: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEvents.map((event) => (
                      <tr 
                        key={event.id}
                        onClick={() => onOpenRiskAnalysis(event)}
                        className={`event-row ${getDateStatus(event.date)}`}
                      >
                  <td style={{ fontSize: '14px', fontWeight: '600' }}>
                    <div>{event.name}</div>
                    {event.ai_review_status && (
                      <span style={{ color: reviewColor(event.ai_review_status), fontSize: '11px', fontWeight: '600' }}>
                        {reviewLabel(event.ai_review_status)}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {formatAllDates(event)}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {event.location || 'No location specified'}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {event.supplier || 'Not specified'}
                  </td>
                  <td style={{ fontSize: '12px' }}>
                    {event.responsible_technician || 'Not specified'}
                  </td>
                  <TravelCell event={event} />
                  <QuickNotesCell event={event} onSave={handleQuickNotesSave} />
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '11px' }}
                        onClick={() => setChecklistEvent(event)}
                        title="Event tasks"
                      >
                        <ListChecks size={12} />
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '11px' }}
                        onClick={() => handleEditEvent(event)}
                        title="Edit Event"
                      >
                        <Edit size={12} />
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '4px 8px', fontSize: '11px' }}
                        onClick={() => handleDeleteEvent(event.id)}
                        title="Delete Event"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Historic Events */}
            {historicEvents.length > 0 && (
              <div className="events-table-container">
                <h3 style={{ marginBottom: '15px' }}>Historic Events (&gt; 1 month ago)</h3>
                <table className="table events-table">
                  <thead>
                    <tr style={{ opacity: 0.8 }}>
                      <th style={{ minWidth: '200px' }}>Name</th>
                      <th style={{ minWidth: '120px' }}>Date</th>
                      <th style={{ minWidth: '180px' }}>Location</th>
                      <th style={{ minWidth: '180px' }}>Supplier</th>
                      <th style={{ minWidth: '180px' }}>Technician</th>
                      <th style={{ minWidth: '180px' }}>Travel</th>
                      <th style={{ minWidth: '220px' }}>Quick Notes</th>
                      <th style={{ minWidth: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicEvents.map((event) => (
                      <tr 
                        key={event.id}
                        onClick={() => onOpenRiskAnalysis(event)}
                        className={`event-row ${getDateStatus(event.date)} historic`}
                      >
                        <td style={{ fontSize: '14px', fontWeight: '600' }}>
                          <div>{event.name}</div>
                          {event.ai_review_status && (
                            <span style={{ color: reviewColor(event.ai_review_status), fontSize: '11px', fontWeight: '600' }}>
                              {reviewLabel(event.ai_review_status)}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {formatAllDates(event)}
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {event.location || 'No location specified'}
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {event.supplier || 'Not specified'}
                        </td>
                        <td style={{ fontSize: '12px' }}>
                          {event.responsible_technician || 'Not specified'}
                        </td>
                        <TravelCell event={event} />
                        <QuickNotesCell event={event} onSave={handleQuickNotesSave} />
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => setChecklistEvent(event)}
                              title="Event tasks"
                            >
                              <ListChecks size={12} />
                            </button>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => handleEditEvent(event)}
                              title="Edit Event"
                            >
                              <Edit size={12} />
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => handleDeleteEvent(event.id)}
                              title="Delete Event"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <EventModal
          event={editingEvent}
          onSave={handleSaveEvent}
          onClose={() => setShowModal(false)}
        />
      )}
      {checklistEvent && <EventChecklistModal event={checklistEvent} onClose={() => setChecklistEvent(null)} onEdit={() => { setEditingEvent(checklistEvent); setChecklistEvent(null); setShowModal(true); }} />}
    </div>
  );
};

export default EventList;
